import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import axios from 'axios';

import API_BASE from '../../utils/api';
const API = API_BASE;

const examTypeLabels = {
  unit_test: 'Unit Test', mid_term: 'Mid Term', quarterly: 'Quarterly',
  half_yearly: 'Half Yearly', annual: 'Annual', assignment: 'Assignment'
};

const Exams = () => {
  const [pendingExams, setPendingExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [results, setResults] = useState([]);
  const [examDetail, setExamDetail] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPending = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/exams/review/pending`, { headers });
      setPendingExams(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const viewResults = async (examId) => {
    try {
      const res = await axios.get(`${API}/exams/${examId}/results`, { headers });
      setExamDetail(res.data.exam);
      setResults(res.data.results);
      setSelectedExam(examId);
    } catch (err) { console.error(err); }
  };

  const handleApprove = async () => {
    if (!window.confirm('Approve all marks for this exam? Parents will be able to see them.')) return;
    try {
      await axios.put(`${API}/exams/${selectedExam}/approve`, {}, { headers });
      showMsg('success', 'Marks approved! Now visible to parents.');
      setSelectedExam(null);
      fetchPending();
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!rejectRemarks.trim()) { showMsg('error', 'Please provide a rejection reason'); return; }
    try {
      await axios.put(`${API}/exams/${selectedExam}/reject`, { reviewRemarks: rejectRemarks }, { headers });
      showMsg('success', 'Marks rejected. Teacher will need to re-enter.');
      setShowRejectModal(false);
      setRejectRemarks('');
      setSelectedExam(null);
      fetchPending();
    } catch (err) {
      showMsg('error', err.response?.data?.error || 'Failed to reject');
    }
  };

  const getPassFail = (marks, total, passing) => {
    if (marks === undefined || marks === null) return '-';
    return marks >= passing ? <span className="text-green-600 font-medium">Pass</span> : <span className="text-red-600 font-medium">Fail</span>;
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">📋 Exam Review & Approval</h1>
              <p className="text-gray-500 mt-1">Review marks submitted by teachers and approve for parents</p>
            </div>
            <div className="flex items-center gap-4">
              <button onClick={fetchPending} disabled={loading} className="text-indigo-600 hover:text-indigo-800 text-sm font-medium flex items-center gap-1 disabled:opacity-50">
                🔄 Refresh
              </button>
              <ProfileDropdown />
            </div>
          </div>

          {message.text && (
            <div className={`mb-4 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
              {message.text}
            </div>
          )}

          {/* If viewing results */}
          {selectedExam && examDetail ? (
            <div>
              <button onClick={() => setSelectedExam(null)} className="mb-4 text-indigo-600 hover:text-indigo-800 font-medium">
                ← Back to list
              </button>
              <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">{examDetail.examName}</h2>
                    <p className="text-gray-500">{examDetail.subject} | {examDetail.classId?.className} - {examDetail.classId?.section} | {new Date(examDetail.date).toLocaleDateString()}</p>
                    <p className="text-sm text-gray-400 mt-1">Total: {examDetail.totalMarks} | Pass: {examDetail.passingMarks} | By: {examDetail.createdBy?.name}</p>
                  </div>
                  {results.some(r => r.status === 'submitted') && (
                    <div className="flex gap-2">
                      <button onClick={handleApprove}
                        className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 font-medium">
                        ✅ Approve All
                      </button>
                      <button onClick={() => setShowRejectModal(true)}
                        className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 font-medium">
                        ❌ Reject
                      </button>
                    </div>
                  )}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-blue-700">{results.length}</p>
                    <p className="text-xs text-blue-600">Total Students</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-green-700">{results.filter(r => r.marksObtained >= examDetail.passingMarks).length}</p>
                    <p className="text-xs text-green-600">Passed</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-red-700">{results.filter(r => r.marksObtained < examDetail.passingMarks).length}</p>
                    <p className="text-xs text-red-600">Failed</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-purple-700">{results.length > 0 ? Math.max(...results.map(r => r.marksObtained)) : 0}</p>
                    <p className="text-xs text-purple-600">Highest</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                    <p className="text-xl font-bold text-amber-700">{results.length > 0 ? Math.round(results.reduce((s, r) => s + r.marksObtained, 0) / results.length) : 0}</p>
                    <p className="text-xs text-amber-600">Average</p>
                  </div>
                </div>

                {/* Results table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="p-3 text-sm font-semibold text-gray-600">#</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Roll No</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Student</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Marks</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Grade</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Result</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Status</th>
                        <th className="p-3 text-sm font-semibold text-gray-600">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.map((r, i) => (
                        <tr key={r._id} className={`border-b hover:bg-gray-50 ${r.marksObtained < examDetail.passingMarks ? 'bg-red-50' : ''}`}>
                          <td className="p-3 text-sm">{i + 1}</td>
                          <td className="p-3 text-sm font-medium">{r.studentId?.rollNumber}</td>
                          <td className="p-3 text-sm">{r.studentId?.name}</td>
                          <td className="p-3 text-sm font-bold">{r.marksObtained}/{examDetail.totalMarks}</td>
                          <td className="p-3 text-sm font-medium">{r.grade}</td>
                          <td className="p-3 text-sm">{getPassFail(r.marksObtained, examDetail.totalMarks, examDetail.passingMarks)}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              r.status === 'approved' ? 'bg-green-100 text-green-700' :
                              r.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>{r.status}</span>
                          </td>
                          <td className="p-3 text-sm text-gray-500">{r.remarks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Reject Modal */}
              {showRejectModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl p-6 w-full max-w-md">
                    <h3 className="text-lg font-bold text-red-700 mb-4">❌ Reject Marks</h3>
                    <p className="text-sm text-gray-600 mb-3">Provide a reason for rejection. The teacher will need to correct and resubmit.</p>
                    <textarea value={rejectRemarks} onChange={(e) => setRejectRemarks(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 mb-4" rows="3" placeholder="Reason for rejection..." />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => { setShowRejectModal(false); setRejectRemarks(''); }}
                        className="px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                      <button onClick={handleReject}
                        className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">Reject</button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Pending Exams List */
            <div>
              {loading ? <p className="text-center py-10 text-gray-500">Loading...</p> : (
                pendingExams.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <p className="text-5xl mb-3">📭</p>
                    <p className="text-lg">No exam results pending review</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pendingExams.map(item => (
                      <div key={item.exam._id}
                        onClick={() => viewResults(item.exam._id)}
                        className={`bg-white rounded-xl shadow p-5 cursor-pointer hover:shadow-lg transition-shadow border-l-4 ${
                          item.status === 'submitted' ? 'border-blue-500' : 'border-green-500'
                        }`}>
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-bold text-gray-800">{item.exam.examName}</h3>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            item.status === 'submitted' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {item.status === 'submitted' ? '⏳ Pending Review' : '✅ Approved'}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>📚 {item.exam.subject}</p>
                          <p>🏫 {item.exam.classId?.className} - {item.exam.classId?.section}</p>
                          <p>📅 {new Date(item.exam.date).toLocaleDateString()}</p>
                          <p>👨‍🏫 {item.exam.createdBy?.name}</p>
                          <p>📊 {item.submittedCount > 0 ? `${item.submittedCount} submitted` : ''} {item.approvedCount > 0 ? `${item.approvedCount} approved` : ''} / {item.totalStudents} students</p>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          {examTypeLabels[item.exam.examType]} | Total: {item.exam.totalMarks}
                        </p>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Exams;
